'use client';

import { useCallback } from 'react';
import { useState } from 'react';

import type { z } from 'zod';

import { useSignInWithEmailPassword } from '@kit/supabase/hooks/use-sign-in-with-email-password';

import type { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { AuthErrorAlert } from './auth-error-alert';
import { PasswordSignInForm } from './password-sign-in-form';

export function PasswordSignInContainer({
  onSignIn,
}: {
  onSignIn?: (userId?: string) => unknown;
}) {
  const signInMutation = useSignInWithEmailPassword();
  const [signInError, setSignInError] = useState<string | undefined>();
  const isLoading = signInMutation.isPending;

  const onSubmit = useCallback(
    async (
      credentials: z.infer<typeof PasswordSignInSchema> & { username: string },
    ) => {
      setSignInError(undefined);

      const { username, ...signInCredentials } = credentials;

      if (username.trim().length > 0) {
        return;
      }

      try {
        const data = await signInMutation.mutateAsync(signInCredentials);

        if (onSignIn) {
          const userId = data?.user?.id;

          onSignIn(userId);
        }
      } catch (error) {
        setSignInError(
          error instanceof Error ? error.message : 'default',
        );
      }
    },
    [onSignIn, signInMutation],
  );

  return (
    <>
      <AuthErrorAlert error={signInError ?? signInMutation.error} />

      <PasswordSignInForm onSubmit={onSubmit} loading={isLoading} />
    </>
  );
}
